// Types
import { Order } from "@/types/supabase";

// Libs
import { useEffect, useState } from "react";
import { parseISO } from "date-fns";

// Hooks
import { useOrdersApi } from "@/hooks/useOrdersApi";

// Components
import {
  Table,
  TableBody,
  TableCell,
  TableCaption,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/atoms/table";
import StatusBadge from "@/components/orders/StatusBadge";
import { Badge } from "@/components/atoms/badge";

export default function OrderStateHistory({ order }: { order: Order }) {
  const { fetchOrderStateHistory } = useOrdersApi();
  const [orderStateHistory, setOrderStateHistory] = useState<any[]>([]);
  const formatUtcToCST = (utcTimestamp: string): string => {
    try {
      let date: Date;

      // Handle both ISO and non-ISO date strings
      if (utcTimestamp.includes("T")) {
        // ISO format - use parseISO
        let isoString = utcTimestamp;

        // Only append "Z" if the timestamp doesn't already have timezone information
        const hasTimezone = /[+-]\d{2}:?\d{2}$|Z$/.test(utcTimestamp);

        if (!hasTimezone) {
          isoString = utcTimestamp + "Z";
        }

        date = parseISO(isoString);
      } else {
        // Non-ISO format - use Date constructor as fallback
        date = new Date(utcTimestamp);
      }

      if (isNaN(date.getTime())) {
        return utcTimestamp;
      }

      // Use built-in JavaScript timezone conversion (handles DST automatically)
      const timeZone = "America/El_Salvador";

      // Create a formatter for the date part
      const dateFormatter = new Intl.DateTimeFormat("es-SV", {
        timeZone,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      // Create a formatter for the time part
      const timeFormatter = new Intl.DateTimeFormat("es-SV", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      // Format the date and time separately for more control
      const formattedDate = dateFormatter.format(date);
      const formattedTime = timeFormatter.format(date);

      // Convert date from "dd/mm/yyyy" to "dd-mm-yyyy" format
      const dateWithDashes = formattedDate.replace(/\//g, "-");

      // Clean up the time format (remove dots from a.m./p.m.)
      const cleanTime = formattedTime.replace(
        /\s(a\.m\.|p\.m\.)/g,
        (period) => ` ${period.replace(/\./g, "")}`,
      );

      return `${dateWithDashes} ${cleanTime}`;
    } catch (error) {
      return utcTimestamp;
    }
  };

  const getOrderStateHistory = async () => {
    const data = await fetchOrderStateHistory(order?.id || 0);
    if (data) {
      // Sort by started_at from newest to oldest
      const sortedData = data.sort((a: any, b: any) => {
        return (
          new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      });
      setOrderStateHistory(sortedData);
    }
  };

  useEffect(() => {
    getOrderStateHistory();
  }, [order]);
  return (
    <article className="p-6 rounded-2xl space-y-4 bg-white shadow-md flex-1">
      <header className="font-semibold text-black flex justify-between items-center">
        Historial de cambios
      </header>
      <section>
        <Table>
          <TableCaption className="sr-only">
            A list of your recent invoices.
          </TableCaption>
          <TableHeader className="bg-gray-100 hidden md:table-header-group">
            <TableRow>
              <TableHead className="first:pl-4 last:pr-4 first:rounded-tl-lg last:rounded-tr-lg">
                Fecha
              </TableHead>
              <TableHead className="first:pl-4 last:pr-4 first:rounded-tl-lg last:rounded-tr-lg">
                Usuario
              </TableHead>
              <TableHead className="md:text-right first:pl-4 last:pr-4 first:rounded-tl-lg last:rounded-tr-lg">
                Estado
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orderStateHistory.length > 0 ? (
              orderStateHistory.map((item, index) => (
                <TableRow
                  key={index}
                  className="flex flex-col md:table-row py-3 first:pt-0"
                >
                  <TableCell className="font-medium first:pl-4 last:pr-4">
                    <span className="md:hidden font-semibold text-black mr-1 inline-block">
                      Fecha:
                    </span>
                    {formatUtcToCST(item.started_at)}
                  </TableCell>
                  <TableCell>
                    <span className="md:hidden font-semibold text-black mr-1 inline-block">
                      Usuario:
                    </span>
                    {item.user?.name
                      ? item.user?.name
                      : item.status === "pending" || item.status === "accepted"
                        ? "Sistema"
                        : "Driver"}
                    {item.user?.role && (
                      <Badge variant="outline" className="ml-2">
                        {item.user?.role}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="md:text-right">
                    <span className="md:hidden font-semibold text-black mr-1 inline-block">
                      Estado:
                    </span>
                    <StatusBadge status={item.status} type="order" />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center">
                  No hay historial de cambios
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </section>
    </article>
  );
}
